import React, { useState } from 'react';
import { getTodayISO } from '../lib/dateUtils';

const SERVICE_TYPES = [
  { value: 'repair', label: 'Repair' },
  { value: 'oil change', label: 'Oil Change' },
  { value: 'registration', label: 'Registration' },
  { value: 'tire change', label: 'Tire Change' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'battery', label: 'Battery' },
  { value: 'brake', label: 'Brake Service' },
  { value: 'air conditioning', label: 'Air Conditioning' },
  { value: 'lights', label: 'Lights' },
  { value: 'other', label: 'Other' },
];

export type ServiceBookFormData = {
  mileage: string;
  date: string;
  price: string;
  notes: string;
  type: string;
};

export type ServiceBookSubmitData = {
  mileage: number;
  date: string;
  price: number;
  notes: string;
  type: string;
};

interface ServiceBookFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ServiceBookSubmitData) => void;
  initialData?: Partial<ServiceBookSubmitData>;
}

const ServiceBookForm: React.FC<ServiceBookFormProps> = ({ open, onClose, onSubmit, initialData }) => {
  const [form, setForm] = useState<ServiceBookFormData>({
    mileage: initialData?.mileage?.toString() || '',
    date: initialData?.date || getTodayISO(),
    price: initialData?.price?.toString() || '',
    notes: initialData?.notes || '',
    type: initialData?.type || SERVICE_TYPES[0].value,
  });
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (open && initialData) {
      setForm({
        mileage: initialData.mileage?.toString() || '',
        date: initialData.date || getTodayISO(),
        price: initialData.price?.toString() || '',
        notes: initialData.notes || '',
        type: initialData.type || SERVICE_TYPES[0].value,
      });
    }
  }, [initialData, open]);

  if (!open) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.mileage || !form.date || !form.price || !form.type) {
      setError('Sva polja osim napomene su obavezna.');
      return;
    }
    // Allow comma as decimal separator
    const mileageNum = Number(form.mileage.replace(',', '.'));
    const priceNum = Number(form.price.replace(',', '.'));
    if (isNaN(mileageNum) || isNaN(priceNum) || mileageNum <= 0 || priceNum <= 0) {
      setError('Kilometraža i cena moraju biti brojevi veći od nule. Koristite zarez za decimale, npr. 12,34.');
      return;
    }
    setError('');
    onSubmit({
      ...form,
      mileage: mileageNum,
      price: priceNum,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-theme-primary rounded-lg p-6 shadow-lg w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-theme-primary">{initialData ? 'Edit' : 'Add'} Service Entry</h2>
          <button onClick={onClose} className="text-theme-secondary hover:text-theme-primary">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-theme-secondary mb-1">Mileage*</label>
            <input
              type="text"
              name="mileage"
              value={form.mileage}
              onChange={handleChange}
              className="w-full px-3 py-2 rounded-md border border-theme-primary bg-theme-secondary text-theme-primary focus:outline-none"
              required
              inputMode="decimal"
              pattern="[0-9]+([,\.][0-9]{1,2})?"
              placeholder="npr. 120000,5"
            />
          </div>
          <div>
            <label className="block text-theme-secondary mb-1">Date*</label>
            <input
              type="date"
              name="date"
              value={form.date}
              onChange={handleChange}
              className="w-full px-3 py-2 rounded-md border border-theme-primary bg-theme-secondary text-theme-primary focus:outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-theme-secondary mb-1">Price (RSD)*</label>
            <input
              type="text"
              name="price"
              value={form.price}
              onChange={handleChange}
              className="w-full px-3 py-2 rounded-md border border-theme-primary bg-theme-secondary text-theme-primary focus:outline-none"
              required
              inputMode="decimal"
              pattern="[0-9]+([,\.][0-9]{1,2})?"
              placeholder="npr. 5000,50"
            />
          </div>
          <div>
            <label className="block text-theme-secondary mb-1">Type*</label>
            <select
              name="type"
              value={form.type}
              onChange={handleChange}
              className="w-full px-3 py-2 rounded-md border border-theme-primary bg-theme-secondary text-theme-primary focus:outline-none"
              required
            >
              {SERVICE_TYPES.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-theme-secondary mb-1">Notes</label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              className="w-full px-3 py-2 rounded-md border border-theme-primary bg-theme-secondary text-theme-primary focus:outline-none"
              rows={2}
            />
          </div>
          {error && <div className="text-red-500 text-sm">{error}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-theme-secondary text-theme-primary border border-theme-primary hover:bg-theme-tertiary">Cancel</button>
            <button type="submit" className="px-4 py-2 rounded-md bg-primary-600 text-white font-medium hover:bg-primary-700">{initialData ? 'Save' : 'Add'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceBookForm; 